import type { Project } from '@bigint-project/core';
import type { TrustedCalendarPin } from './calendars.js';
import type { ProjectKnownView } from './view.js';

export const CLOUD_API_VERSION = 1 as const;
export const BPROJ_FORMAT_VERSION = 1 as const;

export type ProjectRole = 'owner' | 'editor' | 'viewer';

export interface ProjectListItem {
  readonly id: string;
  readonly name: string;
  readonly role: ProjectRole;
  readonly serverRevision: number;
  readonly updatedAt: string;
  readonly archivedAt: string | null;
}

export interface ProjectViewDocument {
  readonly known: ProjectKnownView | null;
  readonly unknown: Readonly<Record<string, unknown>>;
  readonly revision: number;
}

export interface ProjectDocument {
  readonly id: string;
  readonly name: string;
  readonly role: ProjectRole;
  readonly formatVersion: typeof BPROJ_FORMAT_VERSION;
  readonly project: Project;
  readonly unknownTopLevelFields: Readonly<Record<string, unknown>>;
  readonly calendarPins?: readonly TrustedCalendarPin[];
  readonly view: ProjectViewDocument;
  readonly serverRevision: number;
  readonly updatedAt: string;
}

export interface CreateProjectRequest {
  readonly name: string;
  readonly clientOperationId: string;
}

export interface ImportProjectRequest {
  readonly file: unknown;
  readonly clientOperationId: string;
}

export interface SaveProjectRequest {
  readonly formatVersion: typeof BPROJ_FORMAT_VERSION;
  readonly project: Project;
  readonly unknownTopLevelFields: Readonly<Record<string, unknown>>;
  readonly clientOperationId: string;
  readonly reason?: string;
}

export interface SaveProjectViewRequest {
  readonly known: ProjectKnownView | null;
  readonly unknown: Readonly<Record<string, unknown>>;
  readonly clientOperationId: string;
}

export interface UpdateProjectMetadataRequest {
  readonly name?: string;
  readonly archived?: boolean;
  readonly clientOperationId: string;
}

export interface ProjectRevisionSummary {
  readonly revision: number;
  readonly actorUserId: string;
  readonly actorDisplayName: string;
  readonly reason: string;
  readonly payloadSha256: string;
  readonly createdAt: string;
}

export interface RestoreProjectRevisionRequest {
  readonly clientOperationId: string;
}
